import { FormikProvider, useFormik } from 'formik'

import { useReselector } from '@/utils/sharedHooks'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { dollarsToCents } from '@/utils/currencyHelpers'
import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'

import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import {
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
} from './helpers'
import { Alert, Form, Text } from '@/ui'
import { TypographyStyles } from '@/styles'

import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'

// TODO: Migrate to V2
import {
  FormikLabelError,
  FormikRadioToggleButton,
  getFieldName,
  makeNumberSchema,
  makeReqBoolSchema,
} from '@/base'

export const Spouse1099Income = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const fp = useReselector(getFinancialProfile)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.spouse1099Income
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      spouseHasOtherIncome: formFlowAnswers.spouseIndividualIncome
        ? true
        : undefined,
      spouseOtherIncome: formFlowAnswers.spouseIndividualIncome,
    },
    onSubmit: async ({ spouseHasOtherIncome, spouseOtherIncome }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          spouseIndividualIncomeInCents: spouseHasOtherIncome
            ? dollarsToCents(spouseOtherIncome)
            : null,
        })
      )

      if (res) {
        setFormFlowAnswers({
          ...formFlowAnswers,
          spouseIndividualIncome: spouseOtherIncome,
        })
        track('spouse 1099 income submitted', {
          spouse_has_1099_income: spouseHasOtherIncome ?? false,
          spouse_1099_income_in_cents: spouseHasOtherIncome
            ? dollarsToCents(spouseOtherIncome)
            : null,
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { values, isSubmitting, isValid, submitForm, validateForm } = formik

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <div className="max-w-3xl mx-auto text-center">
          <Text variant={TypographyStyles.DISPLAY_SM}>
            Does your spouse earn 1099 income?
          </Text>
        </div>

        <div className="max-w-3xl mx-auto text-center">
          <Text variant={TypographyStyles.BODY_LG}>
            This includes work from freelancing, contractor positions and
            short-term gigs.
          </Text>
        </div>

        {error && (
          <Alert
            className="max-w-3xl mx-auto"
            variant="error"
            description={error.message}
          />
        )}

        {/* Question Label */}
        <div className="max-w-3xl mx-auto">
          <FormikLabelError
            name={getFieldName<typeof values>('spouseHasOtherIncome')}
            schema={makeReqBoolSchema()}
          />
        </div>

        {/* Toggle Buttons */}
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row justify-center gap-4">
          <div className="sm:w-1/2 w-full">
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('spouseHasOtherIncome')}
              value
              fullWidth
              onClick={validateForm}
            >
              Yes
            </FormikRadioToggleButton>
          </div>
          <div className="sm:w-1/2 w-full">
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('spouseHasOtherIncome')}
              value={false}
              fullWidth
              onClick={validateForm}
            >
              No
            </FormikRadioToggleButton>
          </div>
        </div>

        {/* Conditional Input */}
        {values.spouseHasOtherIncome && (
          <div className="max-w-2xl mx-auto">
            <Form.Input
              required
              name={getFieldName<typeof values>('spouseOtherIncome')}
              label="Year-to-date 1099 income from other jobs"
              description="This is money earned this year so far through contractor positions, short-term gigs, or other services."
              componentType="currency"
              schema={makeNumberSchema({
                allowedDecimals: 2,
                greaterThanZero: true,
              })}
            />
          </div>
        )}

        {/* FAQs */}
        <TaxProfileAccordions
          faqs={[faqCopy.whySpouseIncomeOtherJobs, faqCopy.howFindSpouse1099]}
        />

        {/* Footer */}
        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
